import React from 'react';
import { router } from '@inertiajs/react';
import { CalendarPlus } from 'lucide-react';

export const EventCard: React.FC<{
  event: Event;
  isUpcoming?: boolean;
}> = ({ event, isUpcoming = false }) => {
  const handleCardClick = () => {
    router.visit(`/events/${event.slug}`);
  };

  const generateCalendarLink = () => {
    if (!event.start_time || !event.end_time) return '#';

    const formatISODate = (dateStr: string) => new Date(dateStr).toISOString().replace(/-|:|\.\d+/g, '');

    const baseUrl = 'https://www.google.com/calendar/render?action=TEMPLATE';
    const params = new URLSearchParams({
      text: event.title,
      dates: `${formatISODate(event.start_time)}/${formatISODate(event.end_time)}`,
      details: event.plain_text_description,
      location: event.location,
    });

    return `${baseUrl}&${params.toString()}`;
  };

  const date = new Date(event.start_time);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();

  const imageSrc =
    event.image_url || event.media_items?.[0]?.attachment_url || 'https://placehold.co/400x300?text=Event';
  const [isPortrait, setIsPortrait] = React.useState(false);

  return (
    <div
      className='h-full bg-card rounded-lg shadow-md overflow-hidden border border-border hover:border-primary hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 flex flex-col cursor-pointer'
      onClick={handleCardClick}
    >
      <div className='relative aspect-[4/3] overflow-hidden bg-muted'>
        {isPortrait && (
          <img
            src={imageSrc}
            alt=''
            aria-hidden='true'
            className='absolute inset-0 w-full h-full object-cover scale-150 blur-2xl'
          />
        )}
        <img
          src={imageSrc}
          alt={event.title}
          onLoad={(e) => setIsPortrait(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth * 1.1)}
          className={`relative w-full h-full ${isPortrait ? 'object-contain' : 'object-cover'}`}
        />
        <div className='absolute top-4 left-4 bg-card/90 backdrop-blur-sm px-3 py-2 rounded-lg text-center shadow-md'>
          <p className='text-primary font-bold text-lg leading-none'>{day}</p>
          <p className='text-muted-foreground text-sm uppercase leading-none'>{month}</p>
        </div>
      </div>
      <div className='p-6 flex flex-col grow'>
        <div className='flex items-center text-sm text-muted-foreground mb-3'>
          <span>{event.location === 'Online' ? 'Virtual' : 'In-Person'}</span>
          <span className='mx-2'>&bull;</span>
          <span>{event.location}</span>
        </div>
        <h3 className='text-xl font-bold text-card-foreground mb-2 font-heading'>{event.title}</h3>
        <p className='text-muted-foreground mb-4 text-sm grow line-clamp-3'>{event.plain_text_description}</p>
        <div className='mt-auto flex items-center justify-between pt-4 border-t border-border/50'>
          <div
            className={`font-semibold transition-colors text-sm ${
              isUpcoming ? 'text-primary hover:text-primary/80' : 'text-muted-foreground/80 hover:text-primary'
            }`}
          >
            {isUpcoming ? 'View Details →' : 'View Details'}
          </div>

          {isUpcoming && (
            <a
              href={generateCalendarLink()}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
              className='flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm font-medium px-3 py-1.5 rounded-md hover:bg-accent group'
              aria-label='Add to calendar'
            >
              <CalendarPlus className='h-4 w-4 group-hover:scale-110 transition-transform' />
              <span>Add to Calendar</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
